/* ═══════════════════════════════════════════════════════════
   ASTROITE — UI string table (chrome only)

   Every leaf is { hi, mr, en }. Use {placeholders} for values
   interpolated at render time (see LangProvider.t).

   NOT here: Peeth / Jyotirlinga content (names, history,
   significance) — that lives in sites.js. Game-internal content
   (level names, milestone toast messages in game/constants.js)
   is intentionally left in its authored language.

   NOTE: hi is authoritative; mr + en were generated and SHOULD
   get a native-speaker review pass before production.
   ═══════════════════════════════════════════════════════════ */

export const STRINGS = {
  // ── Shared across screens ───────────────────────────────
  common: {
    loading:        { hi: 'लोड हो रहा है…', mr: 'लोड होत आहे…', en: 'Loading…' },
    back:           { hi: 'वापस', mr: 'मागे', en: 'Back' },
    close:          { hi: 'बंद करें', mr: 'बंद करा', en: 'Close' },
    guest:          { hi: 'अतिथि', mr: 'पाहुणे', en: 'Guest' },
    ramChants:      { hi: 'राम जप', mr: 'राम जप', en: 'Ram Chants' },
    changeLanguage: { hi: 'भाषा बदलें', mr: 'भाषा बदला', en: 'Change language' },
  },

  // Shared info-grid labels (Peeth + Jyotirlinga modals, map popups)
  info: {
    location: { hi: 'स्थान', mr: 'स्थान', en: 'Location' },
    state:    { hi: 'राज्य', mr: 'राज्य', en: 'State' },
    country:  { hi: 'देश',  mr: 'देश',  en: 'Country' },
  },

  // Shared content section headings
  section: {
    history:      { hi: 'इतिहास', mr: 'इतिहास', en: 'History' },
    significance: { hi: 'महत्त्व', mr: 'महत्त्व', en: 'Significance' },
  },

  // ── Home menu ───────────────────────────────────────────
  menu: {
    play:         { hi: 'खेल शुरू करें', mr: 'खेळ सुरू करा', en: 'Play Game' },
    peeths:       { hi: '{total} शक्ति पीठ', mr: '{total} शक्ती पीठे', en: '{total} Shakti Peeths' },
    jyotirlingas: { hi: '{total} ज्योतिर्लिंग', mr: '{total} ज्योतिर्लिंग', en: '{total} Jyotirlingas' },
    map:          { hi: 'मानचित्र', mr: 'नकाशा', en: 'Pilgrimage Map' },
    leaderboard:  { hi: 'लीडरबोर्ड', mr: 'लीडरबोर्ड', en: 'Leaderboard' },
    profile:      { hi: 'प्रोफ़ाइल', mr: 'प्रोफाईल', en: 'Profile' },
    admin:        { hi: 'एडमिन', mr: 'प्रशासक', en: 'Admin' },
    ramLabel:     { hi: 'राम जप', mr: 'राम जप', en: 'Ram Chants' },
    peethsDone:   { hi: '{n} / {total} शक्ति पीठ पूर्ण', mr: '{n} / {total} शक्ती पीठे पूर्ण', en: '{n} / {total} Shakti Peeths' },
    logout:       { hi: 'लॉगआउट', mr: 'लॉगआउट', en: 'Logout' },
    login:        { hi: 'लॉगिन करें', mr: 'लॉगिन करा', en: 'Login' },
  },

  // ── Game screen (DOM text only — engine/canvas untouched) ─
  game: {
    levelBadge:       { hi: 'स्तर {n}', mr: 'स्तर {n}', en: 'Level {n}' },
    roundInfo:        { hi: 'चक्र {current}/{total}', mr: 'फेरी {current}/{total}', en: 'Round {current}/{total}' },
    jyotirlingaPhase: { hi: 'ज्योतिर्लिंग 🔱', mr: 'ज्योतिर्लिंग 🔱', en: 'Jyotirlinga 🔱' },
    overTitle:        { hi: 'हनुमान जी को विश्राम मिला', mr: 'हनुमानजींना विश्रांती मिळाली', en: 'Hanuman Ji is resting' },
    overSubtitle:     { hi: 'थोड़ा विश्राम, फिर जय श्री राम 🙏', mr: 'थोडा विश्राम, मग जय श्री राम 🙏', en: 'A little rest, then Jai Shri Ram 🙏' },
    statObstacles:    { hi: 'बाधाएं पार', mr: 'अडथळे पार', en: 'Obstacles Passed' },
    statTotalRam:     { hi: 'कुल राम जप', mr: 'एकूण राम जप', en: 'Total Ram Chants' },
    replay:           { hi: 'फिर से खेलें', mr: 'पुन्हा खेळा', en: 'Replay' },
    mainMenu:         { hi: 'मुख्य मेनू', mr: 'मुख्य मेनू', en: 'Main Menu' },
    peethUnlocked:    { hi: '{name} अनलॉक!', mr: '{name} अनलॉक!', en: '{name} unlocked!' },
    jyotUnlocked:     { hi: '{name} ज्योतिर्लिंग अनलॉक!', mr: '{name} ज्योतिर्लिंग अनलॉक!', en: '{name} Jyotirlinga unlocked!' },
    dialogue: {
      hi: ['जय श्री राम! 🙏', 'राम राम राम...', 'हनुमान जी प्रसन्न हैं! 🔱', 'आगे बढ़ो भक्त! ⚡', 'राम नाम जपते जाओ! ✨', 'जय बजरंगबली! 🌟', 'पवन पुत्र प्रसन्न! 🏔️'],
      mr: ['जय श्री राम! 🙏', 'राम राम राम...', 'हनुमानजी प्रसन्न आहेत! 🔱', 'पुढे चला भक्त! ⚡', 'राम नाम जपत राहा! ✨', 'जय बजरंगबली! 🌟', 'पवनपुत्र प्रसन्न! 🏔️'],
      en: ['Jai Shri Ram! 🙏', 'Ram Ram Ram...', 'Hanuman Ji is pleased! 🔱', 'Onward, devotee! ⚡', 'Keep chanting Ram! ✨', 'Jai Bajrangbali! 🌟', 'Pawan Putra is pleased! 🏔️'],
    },
  },

  // ── Shakti Peeths gallery ───────────────────────────────
  peeths: {
    title:          { hi: '{total} शक्ति पीठ', mr: '{total} शक्ती पीठे', en: '{total} Shakti Peeths' },
    unlockedCount:  { hi: '{n}/{total} अनलॉक', mr: '{n}/{total} अनलॉक', en: '{n}/{total} unlocked' },
    filterAll:      { hi: 'सभी', mr: 'सर्व', en: 'All' },
    filterUnlocked: { hi: 'अनलॉक ({n})', mr: 'अनलॉक ({n})', en: 'Unlocked ({n})' },
    filterLocked:   { hi: 'बाकी ({n})', mr: 'उरलेले ({n})', en: 'Locked ({n})' },
    emptyUnlocked:  { hi: 'अभी कोई पीठ अनलॉक नहीं', mr: 'अजून कोणतेही पीठ अनलॉक नाही', en: 'No Peeths unlocked yet' },
    emptyAll:       { hi: 'सभी पीठ अनलॉक हो गए!', mr: 'सर्व पीठे अनलॉक झाली!', en: 'All Peeths unlocked!' },
    loadError:      { hi: 'शक्ति पीठ लोड करने में त्रुटि', mr: 'शक्ती पीठ लोड करताना त्रुटी', en: 'Failed to load Shakti Peeths' },
    modalNumber:    { hi: 'शक्ति पीठ #{id}', mr: 'शक्ती पीठ #{id}', en: 'Shakti Peeth #{id}' },
    infoBodyPart:   { hi: 'अंग', mr: 'अंग', en: 'Body Part' },
    infoShakti:     { hi: 'शक्ति', mr: 'शक्ती', en: 'Shakti' },
    infoBhairava:   { hi: 'भैरव', mr: 'भैरव', en: 'Bhairava' },
    unlockAt:       { hi: '{ram} राम जप पर अनलॉक होगा', mr: '{ram} राम जपावर अनलॉक होईल', en: 'Unlocks at {ram} Ram Chants' },
    ramUnlocked:    { hi: '{ram} राम जप — अनलॉक हो गया', mr: '{ram} राम जप — अनलॉक झाले', en: '{ram} Ram Chants — unlocked' },
    ramWillUnlock:  { hi: '{ram} राम जप — अनलॉक होगा', mr: '{ram} राम जप — अनलॉक होईल', en: '{ram} Ram Chants — will unlock' },
    lockBadge:      { hi: '{ram} राम', mr: '{ram} राम', en: '{ram} Ram' },
  },

  // ── Jyotirlingas gallery ────────────────────────────────
  jyoti: {
    title:          { hi: '{total} ज्योतिर्लिंग', mr: '{total} ज्योतिर्लिंग', en: '{total} Jyotirlingas' },
    unlockedCount:  { hi: '{n}/{total} अनलॉक', mr: '{n}/{total} अनलॉक', en: '{n}/{total} unlocked' },
    filterAll:      { hi: 'सभी', mr: 'सर्व', en: 'All' },
    filterUnlocked: { hi: 'अनलॉक ({n})', mr: 'अनलॉक ({n})', en: 'Unlocked ({n})' },
    filterLocked:   { hi: 'बाकी ({n})', mr: 'उरलेले ({n})', en: 'Locked ({n})' },
    emptyUnlocked:  { hi: 'अभी कोई ज्योतिर्लिंग अनलॉक नहीं', mr: 'अजून कोणतेही ज्योतिर्लिंग अनलॉक नाही', en: 'No Jyotirlingas unlocked yet' },
    emptyAll:       { hi: 'सभी ज्योतिर्लिंग अनलॉक!', mr: 'सर्व ज्योतिर्लिंग अनलॉक!', en: 'All Jyotirlingas unlocked!' },
    loadError:      { hi: 'ज्योतिर्लिंग लोड करने में त्रुटि', mr: 'ज्योतिर्लिंग लोड करताना त्रुटी', en: 'Failed to load Jyotirlingas' },
    modalNumber:    { hi: 'ज्योतिर्लिंग #{id}', mr: 'ज्योतिर्लिंग #{id}', en: 'Jyotirlinga #{id}' },
    infoShivaForm:  { hi: 'शिव रूप', mr: 'शिव रूप', en: 'Shiva Form' },
    unlockAt:       { hi: '{ram} राम जप पर अनलॉक होगा', mr: '{ram} राम जपावर अनलॉक होईल', en: 'Unlocks at {ram} Ram Chants' },
    ramUnlocked:    { hi: '{ram} राम जप — अनलॉक हो गया!', mr: '{ram} राम जप — अनलॉक झाले!', en: '{ram} Ram Chants — unlocked!' },
    ramWillUnlock:  { hi: '{ram} राम जप — अनलॉक होगा', mr: '{ram} राम जप — अनलॉक होईल', en: '{ram} Ram Chants — will unlock' },
  },

  // ── Pilgrimage map ──────────────────────────────────────
  map: {
    title:              { hi: 'तीर्थ यात्रा मानचित्र', mr: 'तीर्थयात्रा नकाशा', en: 'Pilgrimage Map' },
    subtitle:           { hi: 'पवित्र तीर्थ स्थल', mr: 'पवित्र तीर्थ स्थळे', en: 'Sacred sites across the land' },
    filterAll:          { hi: 'सभी', mr: 'सर्व', en: 'All' },
    filterPeeths:       { hi: 'शक्ति पीठ', mr: 'शक्ती पीठे', en: 'Shakti Peeths' },
    filterJyoti:        { hi: 'ज्योतिर्लिंग', mr: 'ज्योतिर्लिंग', en: 'Jyotirlingas' },
    loading:            { hi: 'मानचित्र लोड हो रहा है…', mr: 'नकाशा लोड होत आहे…', en: 'Loading map…' },
    popupUnlocked:      { hi: 'अनलॉक', mr: 'अनलॉक', en: 'Unlocked' },
    popupLockedRam:     { hi: '{ram} राम जप', mr: '{ram} राम जप', en: '{ram} Ram Chants' },
    legendPeethOpen:    { hi: 'शक्ति पीठ अनलॉक', mr: 'शक्ती पीठ अनलॉक', en: 'Shakti Peeth unlocked' },
    legendPeethLocked:  { hi: 'शक्ति पीठ बंद', mr: 'शक्ती पीठ बंद', en: 'Shakti Peeth locked' },
    legendJyotOpen:     { hi: 'ज्योतिर्लिंग अनलॉक', mr: 'ज्योतिर्लिंग अनलॉक', en: 'Jyotirlinga unlocked' },
    legendJyotLocked:   { hi: 'ज्योतिर्लिंग बंद', mr: 'ज्योतिर्लिंग बंद', en: 'Jyotirlinga locked' },
  },

  // ── Leaderboard ─────────────────────────────────────────
  leaderboard: {
    title:       { hi: 'लीडरबोर्ड', mr: 'लीडरबोर्ड', en: 'Leaderboard' },
    subtitle:    { hi: 'शीर्ष 100', mr: 'अव्वल 100', en: 'Top 100' },
    yourRank:    { hi: 'आपकी रैंक', mr: 'तुमची रँक', en: 'Your Rank' },
    noData:      { hi: 'अभी कोई डेटा नहीं', mr: 'अजून डेटा नाही', en: 'No data yet' },
    peethsCount: { hi: '{n}/{total} पीठ', mr: '{n}/{total} पीठे', en: '{n}/{total} Peeths' },
    you:         { hi: 'आप', mr: 'तुम्ही', en: 'You' },
    loadError:   { hi: 'लीडरबोर्ड लोड करने में त्रुटि', mr: 'लीडरबोर्ड लोड करताना त्रुटी', en: 'Failed to load leaderboard' },
    prevPage:    { hi: 'पिछला', mr: 'मागील', en: 'Previous' },
    nextPage:    { hi: 'अगला', mr: 'पुढील', en: 'Next' },
    pageOf:      { hi: '{page} / {total}', mr: '{page} / {total}', en: '{page} / {total}' },
  },

  // ── Profile ─────────────────────────────────────────────
  profile: {
    title:              { hi: 'प्रोफ़ाइल', mr: 'प्रोफाईल', en: 'Profile' },
    guestPrompt:        { hi: 'प्रोफ़ाइल देखने के लिए लॉगिन करें', mr: 'प्रोफाईल पाहण्यासाठी लॉगिन करा', en: 'Login to view your profile' },
    goBack:             { hi: 'वापस जाएं', mr: 'मागे जा', en: 'Go Back' },
    defaultName:        { hi: 'भक्त', mr: 'भक्त', en: 'Devotee' },
    memberSince:        { hi: 'सदस्य: {date}', mr: 'सदस्य: {date}', en: 'Member since {date}' },
    peethsCount:        { hi: '{n}/{total} पीठ', mr: '{n}/{total} पीठे', en: '{n}/{total} Peeths' },
    statTotalRam:       { hi: 'कुल राम जप', mr: 'एकूण राम जप', en: 'Total Ram Chants' },
    statBadges:         { hi: 'बैज अर्जित', mr: 'बॅज मिळवले', en: 'Badges Earned' },
    statLastActive:     { hi: 'अंतिम सक्रिय', mr: 'शेवटचे सक्रिय', en: 'Last Active' },
    sectionBadges:      { hi: 'बैज', mr: 'बॅज', en: 'Badges' },
    sectionRecent:      { hi: 'हाल के अनलॉक', mr: 'अलीकडील अनलॉक', en: 'Recent Unlocks' },
    badgeUnlockAt:      { hi: '{ram} राम जप पर अनलॉक', mr: '{ram} राम जपावर अनलॉक', en: 'Unlock at {ram} Ram Chants' },
    loadError:          { hi: 'प्रोफ़ाइल लोड करने में त्रुटि', mr: 'प्रोफाईल लोड करताना त्रुटी', en: 'Failed to load profile' },
  },

  // ── Admin (admin-only, still localised for consistency) ──
  admin: {
    title:             { hi: 'व्यवस्थापक', mr: 'प्रशासक', en: 'Admin' },
    subtitle:          { hi: 'एस्ट्रोइट नियंत्रण केंद्र', mr: 'एस्ट्रोइट नियंत्रण केंद्र', en: 'Astroite Control Centre' },
    tabStats:          { hi: 'सांख्यिकी', mr: 'आकडेवारी', en: 'Stats' },
    tabUsers:          { hi: 'उपयोगकर्ता', mr: 'वापरकर्ते', en: 'Users' },
    tabTools:          { hi: 'उपकरण', mr: 'साधने', en: 'Tools' },
    statTotalUsers:    { hi: 'कुल उपयोगकर्ता', mr: 'एकूण वापरकर्ते', en: 'Total Users' },
    statTotalChants:   { hi: 'कुल राम जप', mr: 'एकूण राम जप', en: 'Total Chants' },
    statTotalSessions: { hi: 'कुल खेल सत्र', mr: 'एकूण खेळ सत्रे', en: 'Total Sessions' },
    statPeethUnlocks:  { hi: 'शक्ति पीठ अनलॉक', mr: 'शक्ती पीठ अनलॉक', en: 'Peeth Unlocks' },
    activeUsers:       { hi: 'सक्रिय उपयोगकर्ता', mr: 'सक्रिय वापरकर्ते', en: 'Active Users' },
    dau:               { hi: 'दैनिक · DAU', mr: 'दैनिक · DAU', en: 'Daily · DAU' },
    mau:               { hi: 'मासिक · MAU', mr: 'मासिक · MAU', en: 'Monthly · MAU' },
    usersTotal:        { hi: 'कुल {n} उपयोगकर्ता', mr: 'एकूण {n} वापरकर्ते', en: '{n} users total' },
    page:              { hi: 'पृष्ठ {n}', mr: 'पृष्ठ {n}', en: 'Page {n}' },
    peethsCount:       { hi: '{n}/{total} पीठ', mr: '{n}/{total} पीठे', en: '{n}/{total} Peeths' },
    noUsers:           { hi: 'कोई उपयोगकर्ता नहीं मिले', mr: 'कोणतेही वापरकर्ते आढळले नाहीत', en: 'No users found' },
    prev:              { hi: '← पिछला', mr: '← मागील', en: '← Prev' },
    next:              { hi: 'अगला →', mr: 'पुढील →', en: 'Next →' },
    toolSeedTitle:     { hi: '{total} शक्ति पीठ बीज', mr: '{total} शक्ती पीठ बीज', en: 'Seed {total} Shakti Peeths' },
    toolSeedSub:       { hi: 'सभी {total} शक्ति पीठ बीजित करें', mr: 'सर्व {total} शक्ती पीठे बीजित करा', en: 'Seed all {total} Shakti Peeths' },
    toolSeedDesc:      { hi: 'सभी {total} शक्ति पीठ डेटाबेस में सम्मिलित करें। पहले से मौजूद पीठ छोड़े जाएंगे।', mr: 'सर्व {total} शक्ती पीठे डेटाबेसमध्ये समाविष्ट करा. आधीपासून असलेली पीठे वगळली जातील.', en: 'Insert all {total} Shakti Peeths into the database. Existing peeths are skipped.' },
    toolSeedNote:      { hi: 'पहले से मौजूद पीठ स्वतः छोड़ दिए जाते हैं।', mr: 'आधीपासून असलेली पीठे आपोआप वगळली जातात.', en: 'Idempotent — existing peeths are skipped automatically.' },
    seeding:           { hi: 'बीजन हो रहा है…', mr: 'बीजन सुरू आहे…', en: 'Seeding…' },
    runSeed:           { hi: 'बीज चलाएं →', mr: 'बीज चालवा →', en: 'Run Seed →' },
    statsLoadFailed:   { hi: 'सांख्यिकी लोड विफल', mr: 'आकडेवारी लोड अयशस्वी', en: 'Stats load failed' },
    usersLoadFailed:   { hi: 'उपयोगकर्ता लोड विफल', mr: 'वापरकर्ते लोड अयशस्वी', en: 'Users load failed' },
    seedResult:        { hi: '{inserted} पीठ बीजित · {skipped} छोड़े गए', mr: '{inserted} पीठे बीजित · {skipped} वगळली', en: 'Seeded {inserted} peeths · {skipped} skipped' },
    seedFailed:        { hi: 'बीजन विफल: {error}', mr: 'बीजन अयशस्वी: {error}', en: 'Seed failed: {error}' },
  },
};
